import { useState } from 'react';
import { Sun, Calendar, Clock, Archive, Check, Trash2, Users } from 'lucide-react';
import QuickActionButton from './QuickActionButton';
import InlineDatePicker from './InlineDatePicker';
import InlineWaitingForm from './InlineWaitingForm';
import { getTodayString } from '../../utils/dateUtils';
import { isDelegated } from '../../hooks/useTasks';

/**
 * Unified status actions: Today, Schedule, Waiting, 1:1, Someday, Done, Delete
 * Same appearance everywhere - standard design with icon + label
 * Supports monochrome mode for reduced visual clutter (gray default, color on hover)
 *
 * Updated to use the new delegation system with delegate objects { uid, name, email, isExternal }
 */
export default function StatusActions({
  task,
  onAddToToday,
  onSchedule,
  onMoveToWaiting,
  onMoveToSomeday,
  onAddToOneOnOne,
  onComplete,
  onDelete,
  disabled = false,
  showDelete = true,
  showDone = true,
  showOneOnOne = false,
  monochrome = false,
  delegates = []
}) {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showWaitingForm, setShowWaitingForm] = useState(false);

  const today = getTodayString();
  const isScheduledForToday = task?.scheduledDate === today || task?.focusedForToday;
  const isWaiting = !!(task?.waitingFor || isDelegated(task));
  const isSomeday = task?.isSomeday;
  const isCompleted = !!task?.completedAt;

  const handleSchedule = (date) => {
    if (date && onSchedule) {
      onSchedule(task.id, date);
      setShowDatePicker(false);
    }
  };

  /**
   * Handle waiting/delegation form submission
   * @param {string|null} waitingFor - Non-delegation waiting reason
   * @param {object|null} delegate - Delegate object { uid, name, email, isExternal }
   * @param {string|null} followUpDate - Follow-up date in YYYY-MM-DD format
   */
  const handleWaiting = (waitingFor, delegate, followUpDate) => {
    if (onMoveToWaiting) {
      onMoveToWaiting(task.id, waitingFor, delegate, followUpDate);
      setShowWaitingForm(false);
    }
  };

  // Show inline date picker
  if (showDatePicker) {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <InlineDatePicker
          onConfirm={handleSchedule}
          onCancel={() => setShowDatePicker(false)}
        />
      </div>
    );
  }

  // Show inline waiting form
  if (showWaitingForm) {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <InlineWaitingForm
          onConfirm={handleWaiting}
          onCancel={() => setShowWaitingForm(false)}
          delegates={delegates}
        />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 flex-wrap" onClick={(e) => e.stopPropagation()}>
      {/* Today */}
      <QuickActionButton
        icon={Sun}
        label="Today"
        colorKey="today"
        isActive={isScheduledForToday}
        disabled={disabled || isScheduledForToday || isCompleted}
        onClick={() => onAddToToday?.(task.id)}
        monochrome={monochrome}
      />

      {/* Schedule */}
      <QuickActionButton
        icon={Calendar}
        label="Schedule"
        colorKey="schedule"
        isActive={task?.scheduledDate && !isScheduledForToday}
        disabled={disabled || isCompleted}
        onClick={() => setShowDatePicker(true)}
        monochrome={monochrome}
      />

      {/* Waiting */}
      <QuickActionButton
        icon={Clock}
        label="Waiting"
        colorKey="waiting"
        isActive={isWaiting}
        disabled={disabled || isCompleted}
        onClick={() => setShowWaitingForm(true)}
        monochrome={monochrome}
      />

      {/* 1:1 - Move to one-on-one */}
      {showOneOnOne && onAddToOneOnOne && (
        <QuickActionButton
          icon={Users}
          label="1:1"
          colorKey="oneOnOne"
          disabled={disabled || isCompleted}
          onClick={onAddToOneOnOne}
          monochrome={monochrome}
        />
      )}

      {/* Someday */}
      <QuickActionButton
        icon={Archive}
        label="Someday"
        colorKey="someday"
        isActive={isSomeday}
        disabled={disabled || isCompleted}
        onClick={() => onMoveToSomeday?.(task.id)}
        monochrome={monochrome}
      />

      {/* Done */}
      {showDone && (
        <QuickActionButton
          icon={Check}
          label={isCompleted ? 'Undo' : 'Done'}
          colorKey="done"
          isActive={isCompleted}
          disabled={disabled}
          onClick={() => onComplete?.(task.id)}
          monochrome={monochrome}
        />
      )}

      {/* Delete */}
      {showDelete && onDelete && (
        <QuickActionButton
          icon={Trash2}
          colorKey="delete"
          iconOnly
          disabled={disabled}
          onClick={() => onDelete?.(task.id)}
          monochrome={monochrome}
        />
      )}
    </div>
  );
}
